import Component from '@glimmer/component'
import SubmitFormRunner from 'teacher-ui-client/utils/submit-form-runner'
import { action } from '@ember/object'
import { service } from '@ember/service'
import { tracked } from '@glimmer/tracking'
import { timeout } from 'ember-concurrency'
import type { IntlService } from 'ember-intl'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type Student from 'teacher-ui-client/models/student'
import {
  FormWrapperSubmitButton,
  type ExternalSubmitState,
} from 'teacher-ui-client/components/form-wrapper/submit-button'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { on } from '@ember/modifier'
import { optional } from 'teacher-ui-client/helpers/optional'
import { t } from 'ember-intl'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'

interface Args {
  subscriptionType: SubscriptionType
  onCancel: () => void
  onSubmit: (studentsToUpdate: Student[]) => Promise<unknown>
  students: Student[]
}

interface Signature {
  Args: Args
}

export class FormWrapperRemoveFromAllClassesForm extends Component<Signature> {
  timeout = 1100
  @service intl!: IntlService
  @service flashQueue!: FlashQueueService

  @tracked submitState: ExternalSubmitState = 'default'

  get formattedSubscriptionType() {
    return this.intl.t(this.args.subscriptionType)
  }

  @action
  submitFailed(studentsToUpdate: Student[]) {
    studentsToUpdate.forEach((student) => {
      student.rollbackRelationships()
    })
    this.flashQueue.addFail({
      subtitle: this.intl.t('manage.manageClasses.students.bulkActions.removeFromAllClasses.error', {
        count: studentsToUpdate.length,
        subscriptionType: this.formattedSubscriptionType,
      }),
    })
  }

  @action
  async submitSuccessful(studentsToUpdate: Student[]) {
    await timeout(this.timeout)
    this.args.onCancel?.()
    this.flashQueue.addSuccess({
      subtitle: this.intl.t('manage.manageClasses.students.bulkActions.removeFromAllClasses.success', {
        count: studentsToUpdate.length,
        subscriptionType: this.formattedSubscriptionType,
      }),
    })
  }

  @action
  onFormSubmit(event: Event) {
    event.preventDefault()
    const studentsToUpdate = this.args.students
    studentsToUpdate.forEach((student) => {
      student.saveRelationships()
    })
    const submitFormRunner = new SubmitFormRunner(this, 'submitState', 1500)
    submitFormRunner.success(() => this.submitSuccessful(studentsToUpdate))
    submitFormRunner.fail(() => this.submitFailed(studentsToUpdate))
    void submitFormRunner.run(this.args.onSubmit, studentsToUpdate)
    return false
  }

  <template>
    <form data-test-remove-from-all-classes-form>
      <div class="p-4">
        <p class="mb-3">{{t
            "manage.manageClasses.students.bulkActions.totalSelectedStudents"
            count=@students.length
            htmlSafe=true
          }}</p>
        <p class="mb-3">{{t
            "manage.manageClasses.students.bulkActions.removeFromAllClasses.confirmation"
            subscriptionType=this.formattedSubscriptionType
          }}</p>
      </div>
      <div class="border-dusty-black-50 border-t p-2 text-right">
        <UiButton data-test-cancel type="reset" {{on "click" (optional @onCancel)}}>
          {{t "manage.manageClasses.students.bulkActions.cancel"}}
        </UiButton>
        <FormWrapperSubmitButton
          data-test-submit
          @defaultText={{t "manage.manageClasses.students.bulkActions.remove"}}
          @state={{this.submitState}}
          @appearance={{ButtonAppearance.regular}}
          @clickAction={{this.onFormSubmit}}
        />
      </div>
    </form>
  </template>
}

export default FormWrapperRemoveFromAllClassesForm
